import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import numeral from 'numeral';
import moment from 'moment';
import withData from '@bd/bdcore-data';
import { css } from '@bd/bdui-styles/emotion';
import { SmoothLink } from '@bd/bdcore-utilities';
import { Link } from '@bd/bdcore-named-routes';
import AgentsList from '../../components/AgentsList';
import ContactModal from '../../components/ContactModal';
import ContactBannerDetail from '../../components/ContactBannerDetail';
import MortgageCalculatorModal from '../../components/MortgageCalculatorModal';
import { colors } from '../../styles';

const styles = css`
  .sold {
    color: ${colors.brand3};
    text-transform:uppercase;
  }
`;

function Overview({ home, community, plan, agents }) {
  let dateStatus;
  if (moment().isBefore(get(home, 'moveInDate'))) {
    dateStatus = `Ready ${moment(get(home, 'moveInDate')).format('MMMM D, YYYY')}`;
  } else {
    dateStatus = '';
  }

  return (
    <div className={styles}>
      <div className="DetailSection">
        <div className="ScrollTarget" id="overview" />
        <div className="DetailOverview">
          <div className="container">
            <div className="row">
              <div className="col-lg-6">
                <div className="DetailOverview_headingWrapper">
                  <h2 className="DetailOverview_heading">
                    {get(home, 'address.streetAddress')}
                    <span className="DetailOverview_subheading">{get(home, 'address.addressLocality')}, {get(home, 'address.addressRegion')} {get(home, 'address.postalCode')}</span>
                  </h2>
                  <div className="DetailOverview_mapButton">
                    <SmoothLink href="#map">
                      <img src="/images/icon-map-pin.svg" alt="" />
                    </SmoothLink>
                  </div>
                </div>
                {
                  get(home, 'price') > 0 &&
                  <div className="DetailOverview_sectionWrapper">
                    <div className="DetailOverview_price">
                      <span>${numeral(get(home, 'price')).format('0,0')}</span>
                    </div>
                  </div>
                }
                <div className="DetailOverview_sectionWrapper">
                  <div className="mr-3 mr-lg-5">
                    <MortgageCalculatorModal
                      price={get(home, 'price', '')}
                    >
                      <img src="/images/icon-calculator.svg" alt="Mortgage Calculator" /> Mortgage Calculator
                    </MortgageCalculatorModal>
                  </div>
                  {
                    (get(home, 'status') || dateStatus) &&
                    <div className="DetailOverview_status">
                      {
                        get(home, 'status') === 'Sold' &&
                        <span>Status: <strong className="sold">{get(home, 'status')}</strong></span>
                      }
                      {
                        get(home, 'status') !== 'Sold' &&
                        <span>Status: {get(home, 'status')}</span>
                      }
                      {
                        dateStatus &&
                        <span>{dateStatus}</span>
                      }
                    </div>
                  }
                </div>
                <ul className="DetailOverview_list">
                  {
                    get(home, 'beds') > 0 &&
                    <li className="DetailOverview_listItem">
                      <span className="DetailOverview_listItemValue">{get(home, 'beds')}</span>
                      <span className="DetailOverview_listItemLabel">Beds</span>
                    </li>
                  }
                  {
                    get(home, 'bathsFull') > 0 &&
                    <li className="DetailOverview_listItem">
                      <span className="DetailOverview_listItemValue">{get(home, 'bathsFull')}</span>
                      <span className="DetailOverview_listItemLabel">Full Baths</span>
                    </li>
                  }
                  {
                    get(home, 'bathsHalf') > 0 &&
                    <li className="DetailOverview_listItem">
                      <span className="DetailOverview_listItemValue">{get(home, 'bathsHalf')}</span>
                      <span className="DetailOverview_listItemLabel">Half Baths</span>
                    </li>
                  }
                  {
                    get(home, 'sqft') > 0 &&
                    <li className="DetailOverview_listItem">
                      <span className="DetailOverview_listItemValue">{numeral(get(home, 'sqft')).format('0,0')}</span>
                      <span className="DetailOverview_listItemLabel">Sq Ft</span>
                    </li>
                  }
                  {
                    !!get(home, 'garages') > 0 &&
                    <li className="DetailOverview_listItem">
                      <span className="DetailOverview_listItemValue">{get(home, 'garages')}</span>
                      <span className="DetailOverview_listItemLabel">Car Garage</span>
                    </li>
                  }
                  {
                    !!get(home, 'stories') > 0 &&
                    <li className="DetailOverview_listItem">
                      <span className="DetailOverview_listItemValue">{get(home, 'stories')}</span>
                      {
                        get(home, 'stories') > 1 &&
                        <span className="DetailOverview_listItemLabel">Stories</span>
                      }
                      {
                        get(home, 'stories') <= 1 &&
                        <span className="DetailOverview_listItemLabel">Story</span>
                      }
                    </li>
                  }
                </ul>
                {
                  get(home, 'home_tour_link') &&
                  <div className="DetailOverview_sectionWrapper">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-small"
                      href={get(home, 'home_tour_link')}
                    >
                      More Information
                    </a>
                  </div>
                }
                <div className="DetailOverview_sectionWrapper">
                  {
                    get(community, 'name') &&
                    <span className="DetailOverview_sectionItem">
                      Community: <Link name="community-detail" community={community}>{get(community, 'name')}</Link>
                    </span>
                  }
                  {
                    get(plan, 'name') &&
                    <span className="DetailOverview_sectionItem">
                      Floor Plan: <Link name="plan-detail" plan={plan}>{get(plan, 'name')}</Link>
                    </span>
                  }
                </div>
                <AgentsList agents={agents} />
                <div className="d-lg-none mb-1">
                  <ContactModal>
                    <span className="btn btn-brand3 w-100">Schedule A Showing</span>
                  </ContactModal>
                </div>
              </div>
              <div className="col-lg-6 d-none d-lg-block">
                <ContactBannerDetail product="home" community={community} agent={agents[0]} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

Overview.propTypes = {
  home: PropTypes.object.isRequired,
  plan: PropTypes.object,
  community: PropTypes.object,
  agents: PropTypes.arrayOf(PropTypes.object),
};

Overview.defaultProps = {
  plan: undefined,
  community: undefined,
  agents: undefined,
};

export default withData(
  {
    community: true,
    home: true,
    plan: true,
  },
  {
    required: {
      home: true,
    },
  },
)(Overview);



// WEBPACK FOOTER //
// ./src/pages/HomeDetail/Overview.js